import { zodResolver } from "@hookform/resolvers/zod";
import type { NotificationDto } from "@ied/shared-types";
import { useQueryClient } from "@tanstack/react-query";
import { type Control, Controller, useForm } from "react-hook-form";
import ReactQuill from "react-quill-new";
import { toast } from "sonner";
import z from "zod";
import { useSaveNotification } from "@/hooks/mutations/use-save-notification";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import "react-quill-new/dist/quill.snow.css";
import { useUpdateNotification } from "@/hooks/mutations/use-update-notification";

const NotificationSchema = z.object({
	heading: z.string().nonempty(),
	sub_heading: z.string().nonempty(),
	urgency: z.enum(["High", "Medium", "Low"]),
	content: z.string().nonempty(),
});

type NotificationFormType = z.infer<typeof NotificationSchema>;

export function CreateNotificationForm({
	onComplete,
	notification,
}: {
	onComplete: () => void;
	notification?: NotificationDto;
}) {
	const isEditing = !!notification;
	const client = useQueryClient();
	const { mutate: saveNotification } = useSaveNotification();
	const { mutate: updateNotification } = useUpdateNotification();
	const {
		register,
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<NotificationFormType>({
		defaultValues: {
			heading: isEditing ? notification.heading : "",
			sub_heading: isEditing ? notification.sub_heading : "",
			content: isEditing ? notification.content : "",
			urgency: "Low",
		},
		resolver: zodResolver(NotificationSchema),
		reValidateMode: "onSubmit",
	});

	const onSubmit = (data: NotificationFormType) => {
		if (isEditing) {
			updateNotification(
				{ content: data.content, id: notification.id },
				{
					onError: (err) => {
						toast(err.message);
					},
					onSuccess: () => {
						toast("Notification updated");
						client.invalidateQueries({ queryKey: ["notifications"] });
						onComplete();
					},
				},
			);
		} else {
			saveNotification(
				{ ...data, sent_status: "Pending" },
				{
					onError: (err) => {
						toast(err.message);
					},
					onSuccess: () => {
						toast("Notification created");
						client.invalidateQueries({ queryKey: ["notifications"] });
						onComplete();
					},
				},
			);
		}
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
			{isEditing ? (
				<p>NOTE: Only the content field can be edited at this time</p>
			) : null}
			<div className="flex flex-col gap-2">
				<Label>Title</Label>
				<Input {...register("heading")} disabled={isEditing} />
				{errors.heading && (
					<p className="text-xs text-red-600">{errors.heading.message}</p>
				)}
			</div>
			<div className="flex flex-col gap-2">
				<Label>Subtitle</Label>
				<Input {...register("sub_heading")} disabled={isEditing} />
				{errors.sub_heading && (
					<p className="text-xs text-red-600">{errors.sub_heading.message}</p>
				)}
			</div>
			<div className="flex flex-col gap-2">
				<Label>Content</Label>
				<RichTextEditorInput control={control} />
				{errors.content && (
					<p className="text-xs text-red-600">{errors.content.message}</p>
				)}
			</div>
			{/* TODO remove this input we dont use it */}
			<div className="flex flex-col gap-2">
				<Label>Urgency</Label>
				<Select {...register("urgency")} defaultValue="Low" disabled>
					<SelectTrigger className="w-[180px]">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="Low">Low</SelectItem>
						<SelectItem value="Medium">Medium</SelectItem>
						<SelectItem value="High">High</SelectItem>
					</SelectContent>
				</Select>
				{errors.urgency && (
					<p className="text-xs text-red-600">{errors.urgency.message}</p>
				)}
			</div>
			<div className="flex justify-end">
				<Button type="submit">
					{isEditing ? "Update Notification" : "Submit Notification"}
				</Button>
			</div>
		</form>
	);
}

function RichTextEditorInput({
	control,
}: {
	control: Control<NotificationFormType>;
}) {
	const modules = {
		toolbar: [
			[{ header: [1, 2, 3, false] }],
			["bold", "italic", "underline", "strike"],
			[{ list: "ordered" }, { list: "bullet" }],
			["link", "image"],
			["clean"],
		],
	};

	return (
		<Controller
			name="content"
			control={control}
			defaultValue=""
			rules={{
				required: "Content is required",
				validate: (value) => {
					// Strip HTML tags to check if there's actual content
					const text = value.replace(/<[^>]*>/g, "").trim();
					return text.length > 0 || "Content cannot be empty";
				},
			}}
			render={({ field }) => (
				<ReactQuill
					theme="snow"
					value={field.value}
					onChange={field.onChange}
					modules={modules}
				/>
			)}
		/>
	);
}
